import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
  useState,
} from 'react'
import { requestRightPanelClose } from '@/lib/panels/right-panel-events'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isObjectEligibleForSelectAll } from '@/components/app/editor/select-all-policy'
import {
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import { MARQUEE_MIN_SIZE_PX } from '../constants/marquee'
import { computePointerDragRect } from '../helpers/drag-rect'
import { computeShapeMarqueeAabb, rectsIntersect } from '../helpers/shape-creation-geometry'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { MarqueeState } from '../types/canvas-drag-state'

/**
 * Select-tool pasteboard gestures: a canvas click while editing shape text backs out of the edit,
 * and a drag draws the selection marquee, which selects (or, with Shift/Ctrl/Cmd, adds) the
 * eligible shapes it touches on release.
 */
export function useMarqueeSelect({
  internalShapeManagerRef,
  canSelectShapes,
  activeIsolationGroupId,
  scaleRef,
  getCanvasPointFromPointerEvent,
  isPointerOnEditingShapeText,
  clearShapeSelectionWithHistory,
  selectShapesWithHistoryAndTextMeta,
  setSelectedGuideIds,
  onBackgroundClick,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  canSelectShapes: boolean
  activeIsolationGroupId: string | null
  scaleRef: MutableRefObject<number>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  isPointerOnEditingShapeText: (clientX: number, clientY: number) => boolean
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
  selectShapesWithHistoryAndTextMeta: (nextShapeIds: string[], afterTextIds: string[]) => void
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
  onBackgroundClick: CenterCanvasConfig['onBackgroundClick']
}) {
  const [marquee, setMarquee] = useState<MarqueeState | null>(null)

  const intersectsRect = useCallback(
    (
      a: { x: number; y: number; width: number; height: number },
      b: { x: number; y: number; width: number; height: number }
    ) => rectsIntersect(a, b),
    []
  )

  const getShapeAabb = useCallback((s: ShapeData) => computeShapeMarqueeAabb(s), [])

  const handleMarqueeSelectPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      // When selection is enabled (Select tool or Shapes tool with no shape type selected),
      // start marquee selection on drag.
      if (!canSelectShapes) return false

      const mgr = internalShapeManagerRef.current
      const isToggleSelectionModifier = e.shiftKey || e.ctrlKey || e.metaKey
      const editingShapeTextId = mgr?.getEditingShapeTextId()

      if (editingShapeTextId) {
        if (isPointerOnEditingShapeText(e.clientX, e.clientY)) {
          return true
        }

        // See the matching comment in the Type-tool branch (`handleTextDragCreatePointerDown` in
        // `use-text-drag-create`) — a canvas click while editing is a deliberate back-out, so
        // exit AND deselect.
        mgr?.exitShapeTextEdit({ activateSelectTool: true })
        clearShapeSelectionWithHistory([])
        return true
      }

      if (e.button === 0 && mgr) {
        if (mgr.isInteracting()) return true

        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

        if (!pt) return true

        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)

        setMarquee({
          pointerId: e.pointerId,
          startCanvasX: pt.canvasX,
          startCanvasY: pt.canvasY,
          currentCanvasX: pt.canvasX,
          currentCanvasY: pt.canvasY,
          preserveSelectionOnClick: isToggleSelectionModifier,
          preserveSelectionOnMiss: isToggleSelectionModifier,
        })
      }

      return true
    },
    [
      getCanvasPointFromPointerEvent,
      canSelectShapes,
      isPointerOnEditingShapeText,
      clearShapeSelectionWithHistory,
      internalShapeManagerRef,
    ]
  )

  // Marquee selection drag
  const handleMarqueeSelectPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (!marquee || e.pointerId !== marquee.pointerId) return false

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return true
      e.preventDefault()
      setMarquee((prev) =>
        prev ? { ...prev, currentCanvasX: pt.canvasX, currentCanvasY: pt.canvasY } : prev
      )
      return true
    },
    [marquee, getCanvasPointFromPointerEvent]
  )

  // Marquee selection release
  const handleMarqueeSelectPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      if (!marquee || e.pointerId !== marquee.pointerId) return false

      const mgr = internalShapeManagerRef.current
      const selectionRect = computePointerDragRect(marquee, scaleRef.current)

      if (selectionRect && mgr) {
        const meetsMinimumSize =
          selectionRect.width >= MARQUEE_MIN_SIZE_PX && selectionRect.height >= MARQUEE_MIN_SIZE_PX
        const allShapes = mgr.getShapes()
        const shapesByIdForMarquee = new Map(allShapes.map((s) => [s.id, s]))
        // Outside isolation, selectShapesWithHistory promotes a hit descendant up to its
        // top-level group (normalizeSelectableIdsForScope) — reject it here if that ancestor
        // (or the shape itself) is locked/frozen, or the promoted group ends up selected even
        // though marquee is meant to skip locked/frozen objects. Inside isolation, no
        // promotion happens (only direct children of the active scope survive normalization),
        // so children of a frozen/locked group must stay marquee-selectable there — their
        // content (fill/stroke/text) is still editable even though geometry edits are blocked.
        const isMarqueeEligible = (s: ShapeData) =>
          isObjectEligibleForSelectAll(s) &&
          (activeIsolationGroupId != null ||
            (!isShapeLockedForUserEdit(s, shapesByIdForMarquee) &&
              !isShapeFrozenForGeometry(s, shapesByIdForMarquee)))
        const hitShapeIds = meetsMinimumSize
          ? allShapes
              .filter((s) => s.hidden !== true && isMarqueeEligible(s))
              .filter((s) => intersectsRect(selectionRect, getShapeAabb(s)))
              .map((s) => s.id)
          : []

        if (hitShapeIds.length > 0) {
          if (marquee.preserveSelectionOnClick) {
            const mergedIds = Array.from(new Set([...mgr.getSelectedShapeIds(), ...hitShapeIds]))

            selectShapesWithHistoryAndTextMeta(mergedIds, [])
          } else {
            selectShapesWithHistoryAndTextMeta(hitShapeIds, [])
          }
        } else if (!marquee.preserveSelectionOnMiss) {
          clearShapeSelectionWithHistory([])
          setSelectedGuideIds(new Set())
          onBackgroundClick?.()
          // WMPCM-11032: an empty-space click collapses every panel, not just the left one.
          requestRightPanelClose()
        }
      } else {
        if (!marquee.preserveSelectionOnClick) {
          clearShapeSelectionWithHistory([])
          setSelectedGuideIds(new Set())
          onBackgroundClick?.()
          // WMPCM-11032: an empty-space click collapses every panel, not just the left one.
          requestRightPanelClose()
        }
      }

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      setMarquee(null)
      return true
    },
    [
      marquee,
      onBackgroundClick,
      intersectsRect,
      getShapeAabb,
      clearShapeSelectionWithHistory,
      selectShapesWithHistoryAndTextMeta,
      activeIsolationGroupId,
      internalShapeManagerRef,
      scaleRef,
      setSelectedGuideIds,
    ]
  )

  return {
    marquee,
    setMarquee,
    handleMarqueeSelectPointerDown,
    handleMarqueeSelectPointerMove,
    handleMarqueeSelectPointerUp,
  }
}
